import * as React from 'react';
import cn from 'classnames';
import { connect, useSelector } from 'react-redux';
import { Form } from 'react-final-form';

import Bottom from './Bottom/Bottom';
import Controls from './Controls/Controls';
import Segments from './Segments/Segments';
import { generateOfferHotelLink, setFieldDataMutator } from '../../utils';
import { redirectToFastSearch, submitForm } from '../../store/actions';
import { SubmitFormHandler } from '../../types';
import { useTheme } from '../../../theme';
import { useConfig } from '../../../context';
import { useRenderers } from '../../../renderProps';
import { isOfferHotels } from '../../store/offerHotels/selectors';
import { SearchFormState } from '../../store/reducers';
import { getSegments, isMultiCity } from '../../store/segments/selectors';
import { getPassengersForRequest, getTotalPassengersCount } from '../../store/passengers/selectors';
import { Segment } from '../../schemas/Segment';
import { PassengerTypeSearchParameter } from '@websky/graphql';
import SimpleLoader from '../../../SimpleLoader';
import { useCurrentUser } from '../../../CurrentUser/CurrentUser.hooks';
import { getModifiedEngineURL } from '../../store/foreignCard/selectors';

const LastSearch = React.lazy(() => import('./LastSearch/LastSearch'));

export interface SearchFormProps {
	className?: string;
	onSubmit?: SubmitFormHandler;
	renderPromoCode?: () => React.ReactElement;
	promoCode?: string;
}

interface StateProps {
	offerHotels: boolean;
	segments: Segment[];
	passengersCount: number;
	passengers: PassengerTypeSearchParameter[];
	isMultiCity: boolean;
}

interface DispatchProps {
	submitForm: typeof submitForm;
	redirectToFastSearch: typeof redirectToFastSearch;
}

type Props = SearchFormProps & DispatchProps & StateProps;

const SearchForm: React.FunctionComponent<Props> = React.memo(
	({
		className,
		submitForm,
		redirectToFastSearch,
		onSubmit,
		renderPromoCode,
		promoCode,
		segments,
		offerHotels,
		passengersCount,
		passengers,
		isMultiCity
	}) => {
		useCurrentUser();
		const {
			global: {
				companyInfo: { iataCode }
			},
			SearchForm: {
				proMode,
				engineURL,
				offerHotelsLink,
				offerHotelsSupplier,
				offerHotelsAdditionalId,
				offerHotelsUtm
			}
		} = useConfig();
		const { SearchForm: css } = useTheme('SearchForm');
		const [loading, setLoading] = React.useState(false);
		const modifiedBasedOnStateEngineURL = useSelector((state: SearchFormState) =>
			getModifiedEngineURL(state, engineURL, iataCode)
		);

		const handleSubmit = async () => {
			if (offerHotels) {
				setLoading(true);

				const generatedHotelLink = generateOfferHotelLink(
					offerHotelsLink,
					segments,
					passengersCount,
					passengers,
					iataCode,
					offerHotelsSupplier,
					offerHotelsAdditionalId,
					offerHotelsUtm
				);

				redirectToFastSearch({
					engineUrl: modifiedBasedOnStateEngineURL,
					hotelsUrl: generatedHotelLink,
					openOfferHotelsStrategy: 'currentTab'
				});

				return;
			}

			if (onSubmit) {
				submitForm(onSubmit);
			} else if (modifiedBasedOnStateEngineURL) {
				redirectToFastSearch({
					engineUrl: modifiedBasedOnStateEngineURL
				});
			}
		};

		const { SearchFormRenderers } = useRenderers();
		const BeforeAdornment = SearchFormRenderers?.BeforeAdornment;

		return (
			<div
				className={cn(css.searchForm, className, {
					[css.searchForm_advanced]: proMode,
					[css.searchForm_multiCity]: isMultiCity
				})}
			>
				<div className={css.wrapper}>
					{BeforeAdornment && (
						<div className={css.beforeAdornment__wrapper}>
							<BeforeAdornment />
						</div>
					)}
					<Form onSubmit={handleSubmit} mutators={{ setFieldData: setFieldDataMutator }} subscription={{}}>
						{({ handleSubmit }) => (
							<form onSubmit={handleSubmit} data-search-form={true} data-test-id="search-form">
								<div className={css.row}>
									<Segments />
									<Controls />
								</div>
								<Bottom renderPromoCode={renderPromoCode} promoCode={promoCode} />
							</form>
						)}
					</Form>
				</div>

				{proMode && (
					<React.Suspense fallback="Loading...">
						<LastSearch />
					</React.Suspense>
				)}

				{loading && <SimpleLoader />}
			</div>
		);
	}
);

const mapStateToProps = (state: SearchFormState): StateProps => ({
	offerHotels: isOfferHotels(state),
	segments: getSegments(state),
	passengersCount: getTotalPassengersCount(state),
	passengers: getPassengersForRequest(state),
	isMultiCity: isMultiCity(state)
});

const mapDispatchToProps = {
	submitForm,
	redirectToFastSearch
};

export default connect(mapStateToProps, mapDispatchToProps)(SearchForm);
